import { useEffect, useState, type RefObject } from "react"

/**
 * Tracks an element's client width. Returns `fallback` until the element is
 * measured (and on the server), so callers can render a stable first frame.
 */
export function useElementWidth(targetRef: RefObject<HTMLElement | null>): number
export function useElementWidth<T>(
  targetRef: RefObject<HTMLElement | null>,
  fallback: T,
): number | T
export function useElementWidth<T>(
  targetRef: RefObject<HTMLElement | null>,
  fallback: T | number = 720,
): number | T {
  const [width, setWidth] = useState<number | T>(fallback)

  useEffect(() => {
    const target = targetRef.current
    if (!target) return

    const updateWidth = () => setWidth(target.clientWidth || fallback)
    updateWidth()

    if (typeof ResizeObserver === "undefined") return
    const observer = new ResizeObserver(updateWidth)
    observer.observe(target)
    return () => observer.disconnect()
  }, [fallback, targetRef])

  return width
}
